import * as React from "react";
import { useRef, useState } from "react";
import {
  Card,
  CardContent,
  Container,
  Grid,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  ListItem,
} from "@mui/material";
import { useCompanyProfileContext } from "../../context/CompanyProfile/CompanyProfileContext";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { UploadLogo } from "../../assets/uploadLogo";
import { DisplayCompaniesList } from "./DisplayCompaniesList";
import { AddCompanyProfile, editCompanyProfile, getLogo, uploadLogo } from "./helper";
import { addCompany, companyList, EditCompany } from "./modal";
import { useUserContext } from "../../context/auth/UserContext";
import DialogBoxComponent from "../../components/DialogBoxComponent";
import CircularProgress from "@mui/material/CircularProgress";
import { useAccessRights } from "../../utils/useAccessRights";




const getFieldControlStyles = () => ({
  backgroundColor: "#F1F5F7",
  ".MuiOutlinedInput-root": {
    border: "none",
    "& fieldset": {
      border: "none",
    },
  },
  borderRadius: "5px",
});

export const CompanyProfilePage = () => {

  const UploadLogoRender: React.FC<{
    onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    logoPreview: string | null;
    onRemove: () => void;
    onClick: () => void;
  }> = ({ onUpload, logoPreview, onRemove, onClick }) => {
    return (
      <>
        {logoPreview ? (
          <Stack direction="row" justifyContent="flex-start">
            <img
              src={logoPreview}
              alt="Uploaded Logo"
              style={{ cursor: "pointer", maxWidth: "100%" }}
              onClick={onClick}
            />
            <IconButton
              onClick={onRemove}
              disabled={!companyProfilePageAccess?.isUpload}
            // Add spacing between the button and the image
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        ) : (
          <IconButton disabled={!companyProfilePageAccess?.isUpload}
            component="label" sx={{ width: "fitContent" }}>
            <UploadLogo />
            <input
              type="file"
              hidden
              accept=".png, .svg, .jpeg, .jpg"
              onChange={onUpload}
            />
          </IconButton>
        )}
      </>
    );
  };


  const {
    selectedCompany,
    setIsListLoading,
    setRefreshTable,
    setSelectedCompany,
  } = useCompanyProfileContext();
  const companyProfilePageAccess = useAccessRights("User Configuration")

  const { userId, companyLogo, userAccess, companyProfileID, setCompanyLogo } = useUserContext();
  const [logo, setLogo] = useState<File | null>(null); // For file
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [resizedLogo, setResizedLogo] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [uniqueIdentifier, setUniqueIdentifier] = useState<string>("");
  const [isEditable, setIsEditable] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isFormLoading, setIsFormLoading] = useState<boolean>(false)
  const [companyProfileValue, setCompanyProfileValue] = useState<any>({
    companyNameUpd: "",
    uniqueIdentifierUpd: "",
  });
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openSuccessDialogBox: false,
  });

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validFormats = [
        "image/png",
        "image/svg+xml",
        "image/jpeg",
        "image/jpg",
      ];
      const image = new Image();
      image.src = URL.createObjectURL(file);
      image.onload = () => {
        if (
          image.width >= 512 &&
          image.height >= 512 &&
          validFormats.includes(file.type)
        ) {
          // const reader = new FileReader();
          // reader.onloadend = () => {
          // if (typeof reader.result === "string") {
          //   setLogo(reader.result); // Store the Base64 string
          // }
          setLogo(file)
          setLogoPreview(image.src);
          setLogoError(null);
          // };
          // reader.readAsDataURL(file); // Convert image file to Base64 string

          const canvas = document.createElement("canvas");
          canvas.width = 51;
          canvas.height = 48;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(image, 0, 0, 51, 48);
            const resizedDataUrl = canvas.toDataURL("image/png");
            setResizedLogo(resizedDataUrl);
            setLogoError(null);
          }

          setLogoPreview(image.src);
        } else {
          setLogoError(
            "Upload an image with at least 512 x 512px dimensions in .PNG, .SVG, or .JPEG format."
          );
          setLogo(null);
          setLogoPreview(null);
          setResizedLogo(null);
        }
      };
    }
  };

  const handleRemoveLogo = () => {
    setLogo(null);
    setResizedLogo(null);
    setLogoPreview(null);
  };

  const handleApply = async () => {
    // console.log("handleApply companyName", companyName, "uniqueIdentifier", uniqueIdentifier);
    setIsListLoading(true);
    let hasError = false
    try {
      const date = new Date();
      const payload: addCompany = {
        companyName: companyName,
        isDeleted: false,
        createDate: date,
        createdBy: userId,
        companyID: uniqueIdentifier,
        roleID: userAccess.roles[0].roleID,
        userID: userId

      };

      const res = await AddCompanyProfile(payload);
      if (!res.succeeded) {
        hasError = true
      }

      if (logo) {
        const resLogo = await uploadLogo(logo, res.data);

        if (!resLogo.succeeded) {
          hasError = true
        }
      }

      if (!hasError) {
        setRefreshTable(true);
        setDialogState({
          title: "Success",
          body: "Company successfully created",
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      } else {
        setDialogState({
          title: "Error",
          body: "Unexpected Error",
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      }
    } finally {
      setRefreshTable(true);
      setLogo(null);
      setResizedLogo(null);
      setLogoPreview(null);
      setCompanyName("");
      setUniqueIdentifier("");
      setIsEditable(false);
    }
  };
  const handleUpdate = async () => {
    setIsListLoading(true);
    // console.log("handleApply companyName", companyName, "uniqueIdentifier", uniqueIdentifier);
    try {
      let hasError = false
      const date = new Date();
      const payload: EditCompany = {
        companyProfileID: selectedCompany
          ? selectedCompany.companyProfileID
          : 0,
        companyName: companyName,
        isDeleted: false,
        updateDate: date,
        updatedBy: userId,
        companyID: uniqueIdentifier,
        roleID: userAccess.roles[0].roleID,
        userID: userId
      };

      const res = await editCompanyProfile(payload);
      if (!res.succeeded) {
        hasError = true
      }

      if (logo && selectedCompany) {
        const resLogo = await uploadLogo(logo, selectedCompany?.companyProfileID);
        if (!resLogo.succeeded) {
          hasError = true
        }
      }

      if (!res.hasError) {
        if (selectedCompany?.companyProfileID == companyProfileID) {
          const res_base64Logo = await getLogo(selectedCompany?.companyProfileID);
          if (typeof res_base64Logo === "string") {
            setCompanyLogo(res_base64Logo)
            localStorage.setItem("companyLogo", res_base64Logo)
          }
        }
        setDialogState({
          title: "Success",
          body: "Company successfully updated",
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
        setRefreshTable(true);
      } else {
        setDialogState({
          title: "Error",
          body: "Unexpected Error",
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      }
    } finally {
      setRefreshTable(true);
      setLogo(null);
      setResizedLogo(null);
      setLogoPreview(null);
      setCompanyName("");
      setUniqueIdentifier("");
      setIsEditable(false);
    }
  };

  const handleOpenDialog = () => {
    // console.log("handleOpenDialog", "clicked");

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDialogCancel = () => {
    if (dialogState.title === "Success") {
    }
    setDialogState({
      ...dialogState,
      openSuccessDialogBox: false,
    });
  };

  const handleClear = () => {
    setSelectedCompany(null);
    setLogo(null);
    setCompanyName("");
    setUniqueIdentifier("");
    setResizedLogo("");
  };

  const base64ToFile = (base64: string, fileName: string) => {
    // Split the base64 string to extract the MIME type and the data
    const arr = base64.split(',');
    const mimeMatch = arr[0].match(/data:(.*?);base64/);
    const mimeType = mimeMatch ? mimeMatch[1] : '';  // Extract the MIME type (e.g., image/png)

    // Decode base64 to binary string
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    // Convert binary string to byte array
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    // Create a new File object with the decoded byte array, file name, and MIME type
    return new File([u8arr], fileName, { type: mimeType });
  }


  const resizeBase64Image = (base64: string, width: number, height: number): Promise<string> => {
    return new Promise((resolve) => {
      const image = new Image();
      image.src = base64;

      image.onload = () => {
        // Create a canvas and set the desired dimensions
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(image, 0, 0, width, height); // Resize the image
          const resizedDataUrl = canvas.toDataURL("image/png"); // Convert to base64
          resolve(resizedDataUrl); // Resolve the promise with the resized base64
        }
      };

      image.onerror = () => {
        resolve(base64); // In case of error, return original base64
      };
    });
  };

  const setEditFunction = async () => {
    setIsFormLoading(true)
    try {
      if (selectedCompany) {

        const res_base64Logo = await getLogo(selectedCompany.companyProfileID);
        const fileName = selectedCompany?.logo || 'company-logo.png'; // Use the file name or a default

        if (typeof res_base64Logo === "string") {
          // Pass the base64 and the file name to base64ToFile
          const file = base64ToFile(res_base64Logo, fileName);

          // Store the File object in setLogo
          setLogo(file);

          // Resize the logo to 51x48 and set it for display
          const resizedLogo = await resizeBase64Image(res_base64Logo, 51, 48);

          setResizedLogo(resizedLogo); // Set the resized image for preview
          //store full size logo for preview
          setLogoPreview(res_base64Logo)
        }
        setCompanyName(selectedCompany?.companyName);
        setUniqueIdentifier(selectedCompany.companyID);
      }
    } finally {
      setIsFormLoading(false)
    }
  };

  React.useEffect(() => {
    if (selectedCompany) {

      setEditFunction()
    }
  }, [selectedCompany]);

  return (
    <React.Fragment>
      <Container
        sx={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Container
          sx={{
            height: "100%",
            width: "70%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Card
            sx={{
              width: "100%",
              borderRadius: "10px",
              overflow: "hidden",
              height: "100%",
            }}
          >
            {isFormLoading ? (<ListItem
              sx={{ width: "100%", display: "flex", flexDirection: "column" }}
            >
              <CircularProgress />
            </ListItem>) : (<CardContent sx={{ m: 3, height: "100%" }}>
              <Grid
                container
                spacing={5}
                alignItems="center"
                justifyContent="center"
              >
                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    sx={getFieldControlStyles}
                    label="Company Name"
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={companyName ?? ""}
                    disabled={selectedCompany ? !companyProfilePageAccess?.isUpdate : !companyProfilePageAccess?.isCreate}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    label="Unique Identifier"
                    fullWidth
                    sx={getFieldControlStyles}
                    variant="outlined"
                    value={uniqueIdentifier ?? ""}
                    disabled={selectedCompany ? (!companyProfilePageAccess?.isUpdate || companyProfilePageAccess.isRead) : !companyProfilePageAccess?.isCreate}
                    onChange={(e) => setUniqueIdentifier(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid item xs={12}>
                    <Typography sx={{ opacity: 0.8, m: 1 }}>
                      Upload Picture:
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <UploadLogoRender
                      onUpload={handleLogoUpload}
                      logoPreview={resizedLogo}
                      onRemove={handleRemoveLogo}
                      onClick={handleOpenDialog}

                    />
                  </Grid>
                  {!resizedLogo && !logoError ? (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          opacity: 0.7,
                          m: 1,
                        }}
                      >
                        Upload an image with at least 512 x 512px dimensions in
                        .PNG, .SVG, or .JPEG format.
                      </Typography>
                    </Grid>
                  ) : null}
                  {resizedLogo && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          opacity: 0.7,
                          m: 1,
                        }}
                      >
                        Click on the image to preview
                      </Typography>
                    </Grid>
                  )}
                  {logoError && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          opacity: 0.7,
                          m: 1,
                        }}
                        color="error"
                      >
                        {logoError}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                {/* {resizedLogo && (
                                <Grid item xs={12} sx={{ textAlign: 'center', position: 'relative' }}>
                                    <img
                                        src={resizedLogo}
                                        alt="Resized Logo"
                                        onClick={handleOpenDialog}
                                        style={{ cursor: 'pointer', maxWidth: '100%', borderRadius: '4px' }}
                                    />
                                    <IconButton
                                        sx={{ position: 'absolute', top: 0, right: 0 }}
                                        onClick={handleRemoveLogo}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Grid>
                            )} */}
                {/* {logoError && (
                                <Grid xs={12}>
                                    <Typography color="error">{logoError}</Typography>
                                </Grid>
                            )} */}
                <Grid item xs={12} sx={{ textAlign: "right" }}>
                  {selectedCompany ? (
                    <Stack display="flex" spacing={2}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleClear}
                        sx={{
                          backgroundColor: "rgba(0, 102, 255, 0.1)",
                          color: "#0066FF",
                          "&:hover": {
                            bgcolor: "#0066ff2e",
                          },
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        type="submit"
                        sx={{
                          backgroundColor: "#0066FF",
                          "&:hover": {
                            bgcolor: "#0556d0",
                          },
                        }}
                        onClick={handleUpdate}
                        disabled={
                          !companyName || !uniqueIdentifier || !resizedLogo
                        }
                      >
                        Update
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        backgroundColor: "#0066FF",
                        "&:hover": {
                          bgcolor: "#0556d0",
                        },
                      }}
                      onClick={handleApply}
                      disabled={
                        !companyName || !uniqueIdentifier || logo === null || !companyProfilePageAccess?.isCreate
                      }
                    >
                      Apply
                    </Button>
                  )}
                </Grid>
              </Grid>
            </CardContent>)}


          </Card>
        </Container>
        <Container
          sx={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Card
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ m: 3 }}>
              <DisplayCompaniesList />
            </CardContent>
          </Card>
        </Container>
      </Container>



      {/* Dialog for Logo Preview */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Logo Preview</DialogTitle>
        <DialogContent>
          {logoPreview && (
            <img
              src={logoPreview}
              alt="Logo Preview"
              style={{ maxWidth: "100%" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleDialogCancel}
        />
      }
    </React.Fragment >
  );
};
